import type { RelatedPlace } from "~/lib/api";
import { groupRelatedPlaces, type RelatedPlaceCategory, RELATED_PLACE_CATEGORIES } from "~/lib/relatedPlaces";
import { Icon, type IconType } from "~/components/ui/icons/icon";

interface RelatedPlacesSectionProps {
  relatedPlaces: RelatedPlace[];
  onSelect: (place: RelatedPlace) => void;
  title?: string;
  categories?: RelatedPlaceCategory[];
}

interface RelatedPlaceItemProps {
  place: RelatedPlace;
  icon: IconType;
  onSelect: () => void;
}

function RelatedPlaceItem({ place, icon, onSelect }: RelatedPlaceItemProps) {
  const thumbnail = place.images[0]?.thumb;

  return (
    <button
      type="button"
      onClick={onSelect}
      aria-label={`View ${place.name}`}
      className="flex flex-col items-center gap-1 cursor-pointer transition-transform hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-xl"
    >
      {thumbnail ? (
        <div className="relative size-12 overflow-hidden rounded-xl">
          <img src={thumbnail} alt={place.name} className="size-full object-cover" />
        </div>
      ) : (
        <div className="flex size-12 items-center justify-center rounded-xl bg-muted/50">
          <Icon i={icon} className="size-5 text-muted-foreground" />
        </div>
      )}
      <span className="max-w-12 truncate text-xs text-muted-foreground">{place.name}</span>
    </button>
  );
}

interface PlaceCategoryProps {
  label: string;
  icon: IconType;
  places: RelatedPlace[];
  onSelect: (place: RelatedPlace) => void;
}

function PlaceCategory({ label, icon, places, onSelect }: PlaceCategoryProps) {
  if (places.length === 0) return null;

  const labelId = `related-${label.toLowerCase().replace(/\s+/g, "-")}`;

  return (
    <div className="space-y-2">
      <div
        id={labelId}
        className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wide"
      >
        <Icon i={icon} className="size-3.5" />
        <span>{label}</span>
      </div>
      <div role="group" aria-labelledby={labelId} className="flex gap-3">
        {places.map((place) => (
          <RelatedPlaceItem key={place.id} place={place} icon={icon} onSelect={() => onSelect(place)} />
        ))}
      </div>
    </div>
  );
}

export function RelatedPlacesSection({
  relatedPlaces,
  onSelect,
  title = "Nearby",
  categories = RELATED_PLACE_CATEGORIES,
}: RelatedPlacesSectionProps) {
  if (relatedPlaces.length === 0) return null;

  const grouped = groupRelatedPlaces(relatedPlaces, categories);
  const visibleCategories = categories.filter((cat) => (grouped.get(cat.label)?.length ?? 0) > 0);

  if (visibleCategories.length === 0) return null;

  return (
    <div className="space-y-4">
      <h3 className="text-sm font-medium text-foreground">{title}</h3>
      {visibleCategories.map((category) => (
        <PlaceCategory
          key={category.label}
          label={category.label}
          icon={category.icon}
          places={grouped.get(category.label) ?? []}
          onSelect={onSelect}
        />
      ))}
    </div>
  );
}
